import { memo, useMemo } from 'react';
import { Star } from 'react-feather';
import styled from '@emotion/styled';
import type { ConferenceTag } from '@trinity/chat/types';
import { assertIsGoodQuoteTag, computeTranscriptColor } from '../TaggedMomentBuilder/tag-colors';

type Props = {
  className?: string;
  tag: Pick<ConferenceTag, 'color' | 'name' | 'typeId'>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'style'>;

export const TagBadge = memo(({ tag, className, ...rest }: Props) => {
  const isGoodQuoteTag = useMemo(
    () => assertIsGoodQuoteTag({ name: tag.name, typeId: tag.typeId }),
    [tag.typeId, tag.name]
  );

  const style = useMemo(
    () => ({
      backgroundColor: computeTranscriptColor({ color: tag.color, opacity: 10 }),
      borderColor: computeTranscriptColor({ color: tag.color, opacity: 40 }),
    }),
    [tag.color]
  );

  return (
    <StyledTag style={style} className={className} $clickable={!!rest.onClick} {...rest}>
      {isGoodQuoteTag && <StarIcon size={18} />}
      {tag.name}
    </StyledTag>
  );
});

TagBadge.displayName = 'ProjectClips.TagBadge';

const StyledTag = styled.div<{ $clickable: boolean }>`
  border-radius: 4px;
  padding: 6px;
  border: 1px solid;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-semibold);
  font-size: 14px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const StarIcon = styled(Star)`
  color: var(--gray);
  height: 18px;
  width: 18px;
  margin-right: 4px;
`;
